import { t } from '@lingui/macro';
import React, { FC } from 'react';

import defaultImage from 'common/icons/favicon/favicon-144.png';
import config from 'config/config';
import routes from 'utils/routeTranslator';

type Feedback = {
  value: number;
  comment: string;
  createdAt: string;
  user: {
    name: string;
  };
  provider: {
    name: string;
  };
  categoryName?: string;
};

type Props = {
  slug: string;
  city: string;
  categoryName: string;
  imgUrl: string;
  title: string;
  description: string;
  cityName: string;
  regionName: string;
  lat: number | string;
  long: number | string;
  feedbacks: Feedback[];
  feedbacksCount?: number;
  rating?: number;
  isL2?: boolean;
  lowestPrice?: number;
};

const sanitize = (comment: string): string =>
  comment
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
    .replace(/\f/g, '\\f')
    .replace(/"/g, '\\"');

const feedbackSchema = ({ value, comment, provider, createdAt, categoryName, user }: Feedback) => {
  return {
    '@type': 'Review',
    datePublished: createdAt,
    author: {
      '@type': 'Person',
      name: sanitize(user.name),
    },
    name: `${categoryName}: ${sanitize(provider.name)}`,
    reviewBody: sanitize(comment),
    reviewRating: {
      '@type': 'Rating',
      bestRating: '5',
      ratingValue: value,
      worstRating: '1',
    },
  };
};

const ReviewsSchema: FC<Props> = (props) => {
  const { isL2, slug, city, categoryName, lat, long, feedbacks } = props;
  const routeKey = isL2 ? 'front::categories.type' : 'front::category_city';

  const url =
    config.SITE_URL +
    routes.get(routeKey, {
      slug,
      city,
    });
  const priceRange = props.lowestPrice ? t`Ceny już od ${props.lowestPrice} zł` : t`Zapytaj wykonawców o cenę`;
  const image = props.imgUrl || defaultImage;

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: props.title,
    description: props.description,
    image,
    url: url,
    id: url,
    telephone: '-',
    priceRange: priceRange,
    address: {
      '@type': 'PostalAddress',
      'addressCountry': 'PL',
      addressLocality: props.cityName,
      addressRegion: props.regionName,
    },
    geo: {
      '@type': 'GeoCoordinates',
      'latitude': String(lat),
      'longitude': String(long),
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      'opens': '00:00',
      'closes': '23:59',
    },
  };

  if (feedbacks.length > 0) {
    schema.review = feedbacks.map((feedback) => {
      return feedbackSchema({ ...feedback, categoryName });
    });
  }

  schema.aggregateRating = {
    '@type': 'AggregateRating',
    ratingValue: props.rating || 4.7,
    reviewCount: props.feedbacksCount || 10,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema),
      }}
    />
  );
};

export default ReviewsSchema;
